import { left } from "@popperjs/core";
import { resource } from "../objects/resource";
import { DirectionalHint, ITooltipHostStyles, TooltipDelay, TooltipHost } from "@fluentui/react";

interface SPOSiteCardProps {
  ownedResource: resource;
}

/**
 * SPOSiteCard component for displaying SharePoint site information
 */
export function SPOSiteCard(props: SPOSiteCardProps) {
  const hostStyles: Partial<ITooltipHostStyles> = { 
    root: { display: "inline-block", width: "100%" } 
  };

  // Common label styles
  const labelStyle: React.CSSProperties = {
    fontWeight: "bold",
    textAlign: "right",
    paddingRight: "8px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  };

  const inputStyle: React.CSSProperties = {
    width: "100%"
  };

  const textareaStyle: React.CSSProperties = {
    width: "100%",
    resize: "vertical"
  };

  const rowStyle: React.CSSProperties = {
    marginBottom: "12px",
    alignItems: "center"
  };

  return (
    <div>
      <br></br>
          <table className="table table-bordered">
      <tbody>
        <tr style={{ backgroundColor: "white", textAlign: "justify" }}>
          <td>
            <div style={{ padding: "10px" }}>
      <div>
        <br />
        
        {/* Site URL & Display Name */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Site URL:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={props.ownedResource.siteUrl}
              disabled
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Display Name:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                props.ownedResource.displayName !== ""
                  ? props.ownedResource.displayName
                  : props.ownedResource.siteUrl
              }
              disabled
            />
          </div>
        </div>

        {/* Last Activity Date & Sharing Capability */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Last Activity:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                new Date(props.ownedResource.lastUpdatedFormatted).getFullYear() < 2000
                  ? "Inactive for 5+ years"
                  : new Date(props.ownedResource.lastUpdatedFormatted).toDateString()
              }
              disabled
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Sharing:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                props.ownedResource.sensitivityLabel === "" 
                  ? "Not Set" 
                  : props.ownedResource.sensitivityLabel
              }
              disabled
            />
          </div>
        </div>

        {/* Owner Count & Member Count */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Owners:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={props.ownedResource.owners.length}
              disabled
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Members:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                props.ownedResource.membersCount === null || 
                isNaN(props.ownedResource.membersCount) 
                  ? 0 
                  : props.ownedResource.membersCount
              }
              disabled
            />
          </div>
        </div>

        {/* External Member Count & Last Renewed */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              External Members:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              value={
                props.ownedResource.externalMembersCount === null || 
                isNaN(props.ownedResource.externalMembersCount) 
                  ? 0 
                  : props.ownedResource.externalMembersCount
              }
              disabled
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Last Renewed:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <TooltipHost
              content={
                props.ownedResource.renewActivityLog?.actingUser === "Not Renewed"
                  ? "This resource has not been renewed yet"
                  : `Renewed on ${props.ownedResource.renewActivityLog?.activityDateTime}`
              }
              delay={TooltipDelay.zero}
              directionalHint={DirectionalHint.bottomCenter}
              styles={hostStyles}
            >
              <input
                type="text"
                className="form-control"
                style={inputStyle}
                value={props.ownedResource.renewActivityLog?.actingUser}
                disabled
              />
            </TooltipHost>
          </div>
        </div>

        {/* Site Type & Site Description */}
        <div className="row" style={rowStyle}>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Site Type:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <textarea
              className="form-control"
              style={textareaStyle}
              value={props.ownedResource.siteTemplate.title}
              disabled
              rows={3}
            />
          </div>
          <div className="col-3 col-lg-2">
            <label className="col-form-label" style={labelStyle}>
              Description:
            </label>
          </div>
          <div className="col-3 col-lg-4">
            <textarea
              className="form-control"
              style={textareaStyle}
              value={props.ownedResource.siteTemplate.description}
              disabled
              rows={3}
            />
          </div>
        </div>
        
        <br />
      </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    </div>
  );
}

export default SPOSiteCard;